import { Dialog } from 'primereact/dialog';
import { useState, useEffect } from 'react';
import { Client, ClientDashboardAccessPage, ClientDashboardAccessPermission, ClientDashboardAccessTab } from 'src/pages/client-management/types/client.types';
import { Button } from 'src/components/ui/button';
import { useCreateClientDashboardAccessMutation, useDeleteClientDashboardAccessMutation } from 'src/pages/client-management/endpoints/client.endpoints';

export const getUrlForPage = (page: string) => {
  switch (page) {
    case 'reporting':
      return 'reports-old';
    case 'surveys':
      return 'surveys';
    case 'performance':
      return 'performance';
  }
};

const ACCESS_OPTIONS: ClientDashboardAccessPage[] = [
  {
    name: 'Reporting',
    value: 'reporting',
    description: 'View reporting data',
    tabs: [
      {
        name: 'Reporting',
        value: 'reporting',
        description: 'View reporting data',
        permissions: [],
      },
    ],
  },
  {
    name: 'Surveys',
    value: 'surveys',
    description: 'View surveys and manage surveys',
    tabs: [
      {
        name: 'Surveys',
        value: 'surveys', // includes viewing results
        description: 'View surveys you have access to in a table',
        permissions: [
          {
            name: 'Create Survey',
            value: 'create_survey',
            description: 'Create surveys',
          },
        ],
      },
      {
        name: 'Templates',
        value: 'templates', // disables the create survey button and by default the generate survey button
        description: 'View templates for generating surveys',
        permissions: [],
      },
      {
        name: 'Generate Survey',
        value: 'generate_survey',
        description: 'Use AI to generate surveys',
        permissions: [],
      },
      {
        name: 'Survey Results',
        value: 'surveys',
        description: 'View survey results',
        permissions: [
          {
            name: 'AI Panel',
            value: 'ai_panel',
            description: 'Can have AI panelists answer surveys',
          },
        ],
      },
    ],
  },
  {
    name: 'Performance',
    value: 'performance',
    description: 'View performance data',
    tabs: [
      {
        name: 'Performance',
        value: 'performance',
        description: 'View performance data for your campaigns',
        permissions: [],
      },
    ],
  },
];

const DEFAULT_ACCESS_OPTIONS = [
  {
    page: 'reporting',
    tab: 'reporting',
    permission: null,
  },
];

interface DashboardAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
}

export const DashboardAccessModal = ({ isOpen, onClose, client }: DashboardAccessModalProps) => {
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [localClient, setLocalClient] = useState<Client>(client);
  const [selectedTabs, setSelectedTabs] = useState<{ page: string; tab: string; permission: string | null }[]>(client.permissions.length > 0 ? client.permissions.map((permission) => ({ page: permission.page, tab: permission.tab, permission: permission.permission })) : DEFAULT_ACCESS_OPTIONS);

  const [createDashboardAccess] = useCreateClientDashboardAccessMutation();
  const [deleteDashboardAccess] = useDeleteClientDashboardAccessMutation();

  useEffect(() => {
    setLocalClient(client);
    setSelectedTabs(client.permissions.length > 0 ? client.permissions.map((permission) => ({ page: permission.page, tab: permission.tab, permission: permission.permission })) : DEFAULT_ACCESS_OPTIONS);
  }, [client]);

  const handleSave = async() => {
    setSaveStatus('saving');

    const existingPermissions = client.permissions.map((permission) => ({ page: permission.page, tab: permission.tab, permission: permission.permission }));
    try {
      const newPermissions = selectedTabs.filter((tab) => !existingPermissions.some((permission) => permission.page === tab.page && permission.tab === tab.tab && permission.permission === tab.permission));
      const deletedPermissions = existingPermissions.filter((permission) => !selectedTabs.some((tab) => tab.page === permission.page && permission.tab === tab.tab && permission.permission === tab.permission));

      await Promise.allSettled(
        newPermissions.map((perm) =>
          createDashboardAccess([{
            client: client.ext_client_id,
            page: perm.page,
            tab: perm.tab,
            permission: perm.permission,
          }]))
      );

      await Promise.allSettled(
        deletedPermissions.map((permission) =>
          deleteDashboardAccess({
            dashboard_access_id: client.permissions.find((p) => p.page === permission.page && p.permission === permission.permission && p.tab === permission.tab)?.id ?? '',
          })
        )
      );

      setSaveStatus('saved');
    } catch (error) {
      console.error(error);
      setSaveStatus('idle');
    }
  };

  const handleTabSelection = (page: string, tab: string) => {
    if (selectedTabs.some((selectedTab) => selectedTab.page === page && selectedTab.tab === tab)) {
      setSelectedTabs(selectedTabs.filter((selectedTab) => selectedTab.page !== page || selectedTab.tab !== tab));
    } else {
      setSelectedTabs([...selectedTabs, { page, tab, permission: null }]);
    }
  };

  const handlePermissionSelection = (page: string, tab: string, permission: string) => {
    if (selectedTabs.some((selectedTab) => selectedTab.page === page && selectedTab.tab === tab && selectedTab.permission === permission)) {
      setSelectedTabs(selectedTabs.filter((selectedTab) => selectedTab.page !== page || selectedTab.tab !== tab || selectedTab.permission !== permission));
    } else {
      setSelectedTabs([...selectedTabs, { page, tab, permission }]);
    }
  };

  const dialogHeader = (
    <div className="flex items-center justify-between w-full">
      <span className="text-lg font-medium">
        Set Client Access
      </span>
      <div className="flex items-center gap-2">
        {saveStatus === 'saving' && (
          <span className="text-base text-gray-500 h-6 leading-none mr-4">
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className="text-base text-gray-500 h-6 leading-none mr-4">
            Saved
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      visible={isOpen}
      onHide={onClose}
      header={dialogHeader}
      style={{ width: '50vw', maxWidth: '500px' }}
      contentStyle={{ paddingLeft: 8, paddingRight: 8 }}
      draggable={false}
      resizable={false}
    >
      <div className="flex flex-col md:flex-row gap-4 pt-4 h-[70vh] min-h-[400px] overflow-y-auto">
        <div className="flex flex-col flex-shrink-0 justify-between w-full">
          <div className="overflow-y-auto px-2 md:px-4">
            <div className="space-y-4">
              {ACCESS_OPTIONS.map((page: ClientDashboardAccessPage, index: number) => {
                return (
                  <div
                    key={`${index}${page.value}`}
                    className="flex flex-col"
                  >
                    <h3 className="font-medium px-4 py-2">
                      {page.name}
                      {page.description && (
                        <span className="text-sm font-light text-gray-500 pl-2">
                          {page.description}
                        </span>
                      )}
                    </h3>
                    <div className="flex flex-col px-4">
                      {Array.isArray(page.tabs) && page.tabs.map((tab: ClientDashboardAccessTab) => {
                        return (
                          <>
                            <div
                              key={page.value + tab.value}
                              className="flex items-center gap-2 px-4 pt-2"
                            >
                              <input
                                type="checkbox"
                                id={tab.value}
                                name={tab.name}
                                checked={selectedTabs.some((selectedTab) => selectedTab.page === page.value && selectedTab.tab === tab.value)}
                                onChange={() => handleTabSelection(page.value, tab.value)}
                              />
                              <label
                                htmlFor={tab.value}
                                className="text-md font-medium"
                              >
                                {tab.name}
                                {tab.description && (
                                  <span className="text-sm font-light text-gray-500 pl-2">
                                    {tab.description}
                                  </span>
                                )}
                              </label>

                            </div>
                            <div className="flex gap-2">
                              {Array.isArray(tab.permissions) && tab.permissions.map((permission: ClientDashboardAccessPermission) => {
                                return (
                                  <div
                                    key={page.value + tab.value + permission.value}
                                    className="flex items-center gap-2 px-8"
                                  >
                                    <input
                                      type="checkbox"
                                      id={permission.value}
                                      name={permission.name}
                                      checked={selectedTabs.some((selectedTab) => selectedTab.page === page.value && selectedTab.tab === tab.value && selectedTab.permission === permission.value)}
                                      onChange={() => handlePermissionSelection(page.value, tab.value, permission.value)}
                                    />
                                    <label
                                      htmlFor={permission.value}
                                      className="text-md font-light"
                                    >
                                      {permission.name}
                                      {permission.description && (
                                        <span className="text-sm font-light text-gray-500 pl-2">
                                          {permission.description}
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </>

                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-row justify-end px-4 gap-2 w-full mt-2">
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleSave}
              disabled={saveStatus === 'saving' || saveStatus === 'saved' || selectedTabs.length === 0}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};